import { ROLE, type RoleId } from '@/types/api'

/**
 * Capabilities are what the UI checks; roles are what the backend/DB stores.
 * NEVER gate UI on `roleId === 2` directly. Always go through a capability.
 * See MIGRATION/01-proposal.md §3.2 for the reasoning.
 *
 * Backend @PreAuthorize is still authoritative — this is a UI-only gate.
 */
export type Capability =
  | 'view.dashboard'
  | 'view.own-review-queue' // dashboard review queue. VALIDATOR (6) only. Login already rejects FIELD; legacy VOLUNTEER (5) does not review here.
  | 'view.unresolved-farmers' // GET /dashboard/v3/unknown-farmers. Admin and read-only admin only.
  | 'view.farmers'
  | 'create.farmer'
  | 'edit.farmer'
  | 'delete.farmer'
  | 'approve.farmer'
  | 'view.executives'
  | 'view.volunteers'
  | 'view.validators'
  | 'manage.executives'
  | 'manage.volunteers'
  | 'manage.validators' // Validators admin CRUD — validator management (backend PR #70)
  | 'view.reports'
  | 'export.reports'
  | 'manage.settings'
  | 'view.pii'
  | 'view.attendance' // E6-M1 — see the attendance column + Attendance tab
  | 'force.checkout' // E6-M1 — close an executive's stuck shift from the console
  | 'view.field-ops' // E6-M3 — see the /field-ops/live-map tracking console
  | 'view.digital-agreement' // E2 — view the signed digital consent agreement on farmer detail
  | 'approve.agreement' // E2 — review + approve/reject signed farmer agreements (admin + validator)
  | 'approve.logbook' // per-plot logbook approve/reject. Admin, super admin, VALIDATOR (internal only reach the console). NOT legacy VOLUNTEER.
  | 'unlock.logbook' // unlock an APPROVED plot logbook (signatures are kept). Admin + super admin only.
  | 'view.logbook-queue' // Logbooks tab on the review queue. Same roles as approve.logbook.
  | 'view.audit-queue' // future — auditor role placeholder
  | 'view.staff' // see the Executives/Volunteers/Validators management pages read-only (admin + read-only-admin); write actions on those pages stay gated on the manage.* caps

const ADMIN_CAPS: readonly Capability[] = [
  'view.dashboard',
  'view.unresolved-farmers',
  'view.farmers',
  'create.farmer',
  'edit.farmer',
  'delete.farmer',
  'approve.farmer',
  'view.executives',
  'view.volunteers',
  'view.validators',
  'manage.executives',
  'manage.volunteers',
  'manage.validators',
  'view.reports',
  'export.reports',
  'manage.settings',
  'view.pii',
  'view.attendance',
  'force.checkout',
  'view.field-ops',
  'view.digital-agreement',
  'approve.agreement',
  'approve.logbook',
  'unlock.logbook',
  'view.logbook-queue',
  'view.staff',
]

const VALIDATOR_CAPS: readonly Capability[] = [
  'view.dashboard',
  'view.farmers',
  /*
   * Restores the edit parity the client asked for: farmer registration
   * details, KML boundaries, sowing / transplantation dates, logbook entries
   * and KYC documents. Every backing endpoint permits VALIDATOR and is scope
   * -enforced via @ValidatorScoped (sow_and_reap_backend#147/#149).
   *
   * Includes phase-photo upload/replace: timestamped photos sometimes arrive
   * through another channel and a validator uploads them for the farmer.
   * That is existing sow_and_reap_web behaviour, and the backend scopes it on
   * plotId. Does NOT include delete-KML, which is `delete.farmer`.
   */
  'edit.farmer',
  'approve.farmer',
  'view.reports',
  'export.reports',
  'view.pii', // internal validators need to see full PII to verify
  'view.attendance',
  'force.checkout', // backend scope-enforces to in-scope execs
  'view.digital-agreement', // INTERNAL validators (legacy VOLUNTEER + current VALIDATOR) — backend enforces scope
  /*
   * Agreement review. Unblocks the Agreement Review tab on the review queue
   * (queue-page.tsx) and approve/reject on farmer detail.
   *
   * Backend already permitted VALIDATOR on POST /agreement/queue and the three
   * /sig/{sigId} endpoints, but applied NO scope — so this grant waited on
   * sow_and_reap_backend#148, which scopes the queue via ScopedCriteria and the
   * per-signature endpoints via AgreementSignatureScopeResolver. Validators now
   * see and action only agreements for farmers in their assigned districts.
   */
  'approve.agreement',
  /*
   * Executive picker for farmer assignment. NOT manage.executives — that gates
   * the Executives management page (sidebar.tsx, router.tsx), which the client
   * explicitly excluded. This grants only the read needed to pick an executive
   * when assigning or reassigning a scoped farmer.
   */
  'view.executives',
  /*
   * DELIBERATELY ABSENT — confirmed with the client 2026-09-08 (#154):
   *   manage.validators — validator management is admin/super-admin only
   *   manage.executives — field executive management is admin/super-admin only
   *   manage.volunteers — volunteer management is admin/super-admin only
   *   manage.settings   — phase-deadline config is out of validator scope
   *   view.field-ops    — live map is admin/super-admin only
   *   delete.farmer     — gates delete KML, which the client excluded
   *
   * Removing `view.field-ops` also drops /field-ops/live-map, which is
   * guarded on it at router.tsx. `capabilities.test.ts` asserts each of
   * these stays absent — see the note there before widening this list.
   */
]

/*
 * Read-only admin (internal PR/marketing). Broad VIEW across the console —
 * dashboard, farmers, staff lists, reports (incl. export), attendance, live
 * map, agreements — with ZERO write/manage/settings. `view.pii` is deliberately
 * omitted so farmer PII stays masked in captured screens; flip it on if full
 * console PII visibility is ever wanted. Keep in sync with capabilities.test.ts.
 */
const READ_ONLY_ADMIN_CAPS: readonly Capability[] = [
  'view.dashboard',
  'view.unresolved-farmers',
  'view.farmers',
  'view.executives',
  'view.volunteers',
  'view.validators',
  'view.reports',
  'export.reports',
  'view.attendance',
  'view.field-ops',
  'view.digital-agreement',
  'view.staff', // sees Executives/Volunteers/Validators pages read-only (write actions gated on manage.*)
]

/** Map role → set of capabilities. Add new roles here; add new capabilities in the type. */
export const ROLE_CAPABILITIES: Record<RoleId, readonly Capability[]> = {
  [ROLE.SUPER_ADMIN]: ADMIN_CAPS,
  [ROLE.ADMIN]: ADMIN_CAPS,
  [ROLE.EXECUTIVE]: [], // executives use the mobile app
  [ROLE.FARMER]: [], // farmers use the mobile app
  // Legacy validator role — kept for existing users, still called VOLUNTEER on
  // the backend but treated as "validator" by the Flutter login gate (Phase 0 §9.1).
  [ROLE.VOLUNTEER]: VALIDATOR_CAPS,
  // Current validator role — introduced by backend PR #70. Only INTERNAL
  // validators reach the console (the login gate enforces validatorType);
  // FIELD validators use the separate field PWA. The review queue is this
  // role only: reviewing is not an admin or legacy-volunteer task. Logbook
  // review (spec 2026-10-20) is likewise VALIDATOR only, never VOLUNTEER.
  [ROLE.VALIDATOR]: [
    'view.own-review-queue',
    'approve.logbook',
    'view.logbook-queue',
    ...VALIDATOR_CAPS,
  ],
  // Read-only admin — view-only, no mutations (backend enforces; this gates UI).
  [ROLE.READ_ONLY_ADMIN]: READ_ONLY_ADMIN_CAPS,
}

export function capabilitiesFor(roleId: RoleId | null | undefined): readonly Capability[] {
  if (roleId == null) return []
  return ROLE_CAPABILITIES[roleId] ?? []
}
